"use client";

import React from "react";
import {
  Box,
  Container,
  Grid,
  Typography,
  Button,
  Card,
  CardContent,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  useTheme,
  alpha,
  Stepper,
  Step,
  StepLabel,
  StepContent,
  Avatar,
} from "@mui/material";
import {
  Schedule,
  CheckCircle,
  RocketLaunch,
  DesignServices,
  Build,
  AutoFixHigh,
  // DeploymentUnit,
  Support,
  Speed,
  TrendingUp,
  Groups,
  CloudQueue,
} from "@mui/icons-material";
import PopoverForm from "@/components/PopoverForm";

export default function SaaSPlatforms() {
  const theme = useTheme();

  const challenges = [
    {
      title: "Scaling Infrastructure",
      description:
        "Growing user base putting strain on existing systems and architecture.",
    },
    {
      title: "Manual Processes",
      description: "Time-consuming onboarding, billing, and reporting tasks.",
    },
    {
      title: "Customer Support Burden",
      description:
        "Increasing support tickets and manual response requirements.",
    },
    {
      title: "Security & Compliance",
      description:
        "Data protection, regulatory requirements, and security threats.",
    },
  ];

  const solutions = [
    {
      title: "Custom SaaS Development",
      description:
        "Scalable platforms built with React, Next.js, Node.js, and cloud-native architecture.",
    },
    {
      title: "AI-Driven Automation",
      description:
        "Automated onboarding, billing, support, and reporting workflows.",
    },
    {
      title: "IT Support & Monitoring",
      description:
        "24/7 uptime monitoring, security patches, and performance optimization.",
    },
  ];

  const keyServices = [
    {
      title: "Custom SaaS Development",
      description: "End-to-end development of scalable, secure SaaS platforms",
      features: [
        "React/Next.js Frontend",
        "Node.js Backend",
        "Cloud Architecture",
        "API Development",
      ],
      icon: <Build sx={{ fontSize: 40 }} />,
      color: theme.palette.primary.main,
    },
    {
      title: "Process Automation",
      description: "Streamline operations with intelligent automation",
      features: [
        "Zapier/Make Integrations",
        "AI Bots",
        "API Integrations",
        "Workflow Automation",
      ],
      icon: <AutoFixHigh sx={{ fontSize: 40 }} />,
      color: theme.palette.secondary.main,
    },
    {
      title: "Support & Maintenance",
      description: "Reliable ongoing support for your platform",
      features: [
        "Uptime Monitoring",
        "Issue Resolution",
        "Security Updates",
        "Performance Tuning",
      ],
      icon: <Support sx={{ fontSize: 40 }} />,
      color: theme.palette.info.main,
    },
    {
      title: "Data & Analytics",
      description: "Actionable insights and automated reporting",
      features: [
        "Custom Dashboards",
        "Automated Reports",
        "User Analytics",
        "Business Intelligence",
      ],
      icon: <TrendingUp sx={{ fontSize: 40 }} />,
      color: theme.palette.success.main,
    },
  ];

  const processSteps = [
    {
      label: "Discovery & Strategy",
      description:
        "Deep dive into your business model, target market, and technical requirements to create a winning SaaS strategy.",
      icon: <RocketLaunch />,
    },
    {
      label: "UX/UI & Development",
      description:
        "Design intuitive user experiences and build scalable, secure SaaS architecture with modern technologies.",
      icon: <DesignServices />,
    },
    {
      label: "Automation Setup",
      description:
        "Implement intelligent automation for onboarding, billing, support, and internal workflows.",
      icon: <AutoFixHigh />,
    },
    {
      label: "Testing & Launch",
      description:
        "Comprehensive testing, security audits, and smooth deployment to production environment.",
      icon: <RocketLaunch />,
    },
    {
      label: "Continuous Support",
      description:
        "Ongoing maintenance, feature updates, and performance optimization as you scale.",
      icon: <Support />,
    },
  ];

  const whyChoosePoints = [
    {
      icon: <Speed sx={{ fontSize: 40 }} />,
      title: "Proven SaaS Expertise",
      description:
        "Deep understanding of SaaS business models, subscription management, and scaling challenges.",
    },
    {
      icon: <Groups sx={{ fontSize: 40 }} />,
      title: "Flexible Collaboration",
      description:
        "Remote-first approach with global team, adapting to your timezone and workflow preferences.",
    },
    {
      icon: <PriceCheck sx={{ fontSize: 40 }} />,
      title: "Cost-Effective Quality",
      description:
        "Enterprise-grade solutions at startup-friendly prices, with transparent pricing models.",
    },
    {
      icon: <CloudQueue sx={{ fontSize: 40 }} />,
      title: "Trusted Partner",
      description:
        "Long-term relationships with SaaS startups and scaleups across multiple industries.",
    },
  ];

  const handleConsult = () => {
    window.open("https://calendly.com/linxarlabs-info", "_blank");
  };

  return (
    <Box sx={{ minHeight: "100vh", background: "white" }}>
      {/* Hero Section */}
      <Box
        sx={{
          background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.secondary.main} 100%)`,
          color: "white",
          py: { xs: 8, md: 12 },
          position: "relative",
          overflow: "hidden",
        }}
      >
        <Container maxWidth="lg">
          <Grid container spacing={6} alignItems="center">
            <Grid size={{ xs: 12, md: 7 }}>
              <Typography
                variant="h1"
                sx={{
                  fontSize: { xs: "2.5rem", md: "3.5rem" },
                  fontWeight: 700,
                  lineHeight: 1.2,
                  mb: 3,
                }}
              >
                Web Development & Automation for Scaling SaaS Companies
              </Typography>
              <Typography
                variant="h5"
                sx={{
                  mb: 4,
                  opacity: 0.9,
                  lineHeight: 1.6,
                  fontSize: { xs: "1.25rem", md: "1.5rem" },
                }}
              >
                We help SaaS startups and scaleups build reliable platforms,
                automate workflows, and improve customer experience.
              </Typography>
              <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap" }}>
                <Button
                  variant="contained"
                  size="large"
                  startIcon={<Schedule />}
                  onClick={handleConsult}
                  sx={{
                    bgcolor: "white",
                    color: theme.palette.primary.main,
                    px: 4,
                    py: 1.5,
                    fontSize: "1.1rem",
                    fontWeight: 600,
                    "&:hover": {
                      bgcolor: alpha("#fff", 0.9),
                      transform: "translateY(-2px)",
                    },
                    transition: "all 0.3s ease",
                  }}
                >
                  Book a Free Strategy Call
                </Button>
                {/* <Button
                  variant="outlined"
                  size="large"
                  sx={{
                    borderColor: "white",
                    color: "white",
                    px: 4,
                    py: 1.5,
                    fontSize: "1.1rem",
                    fontWeight: 600,
                    "&:hover": {
                      borderColor: "white",
                      bgcolor: alpha("#fff", 0.1),
                      transform: "translateY(-2px)",
                    },
                    transition: "all 0.3s ease",
                  }}
                >
                  View SaaS Case Studies
                </Button> */}
              </Box>
            </Grid>
            <Grid size={{ xs: 12, md: 5 }}>
              <Box
                sx={{
                  background: alpha("#fff", 0.1),
                  borderRadius: 4,
                  p: 4,
                  backdropFilter: "blur(10px)",
                  border: "1px solid rgba(255,255,255,0.2)",
                }}
              >
                <Typography variant="h5" gutterBottom sx={{ fontWeight: 600 }}>
                  🚀 SaaS Specialists
                </Typography>
                <List>
                  {[
                    "Subscription billing expertise",
                    "Multi-tenant architecture",
                    "User management systems",
                    "API-first development",
                  ].map((item, index) => (
                    <ListItem key={index} sx={{ px: 0 }}>
                      <ListItemIcon sx={{ minWidth: 32 }}>
                        <CheckCircle sx={{ color: "white" }} />
                      </ListItemIcon>
                      <ListItemText primary={item} />
                    </ListItem>
                  ))}
                </List>
              </Box>
            </Grid>
          </Grid>
        </Container>
      </Box>

      {/* Problem -> Solution Section */}
      <Box sx={{ py: { xs: 8, md: 12 } }}>
        <Container maxWidth="lg">
          <Grid container spacing={6}>
            {/* Challenges Column */}
            <Grid size={{ xs: 12, md: 6 }}>
              <Typography
                variant="h3"
                gutterBottom
                sx={{
                  fontWeight: 700,
                  fontSize: { xs: "2rem", md: "2.5rem" },
                  color: theme.palette.error.main,
                }}
              >
                Challenges SaaS Companies Face
              </Typography>
              <Box sx={{ mt: 4 }}>
                {challenges.map((challenge, index) => (
                  <Card
                    key={index}
                    sx={{
                      mb: 3,
                      border: `1px solid ${alpha(theme.palette.error.main, 0.1)}`,
                      background: alpha(theme.palette.error.main, 0.02),
                    }}
                  >
                    <CardContent sx={{ p: 3 }}>
                      <Typography
                        variant="h6"
                        gutterBottom
                        sx={{
                          fontWeight: 600,
                          color: theme.palette.error.main,
                        }}
                      >
                        {challenge.title}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {challenge.description}
                      </Typography>
                    </CardContent>
                  </Card>
                ))}
              </Box>
            </Grid>

            {/* Solutions Column */}
            <Grid size={{ xs: 12, md: 6 }}>
              <Typography
                variant="h3"
                gutterBottom
                sx={{
                  fontWeight: 700,
                  fontSize: { xs: "2rem", md: "2.5rem" },
                  color: theme.palette.success.main,
                }}
              >
                Our Solutions
              </Typography>
              <Box sx={{ mt: 4 }}>
                {solutions.map((solution, index) => (
                  <Card
                    key={index}
                    sx={{
                      mb: 3,
                      border: `1px solid ${alpha(theme.palette.success.main, 0.2)}`,
                      background: alpha(theme.palette.success.main, 0.03),
                      transition: "all 0.3s ease",
                      "&:hover": {
                        transform: "translateX(8px)",
                      },
                    }}
                  >
                    <CardContent sx={{ p: 3 }}>
                      <Typography
                        variant="h6"
                        gutterBottom
                        sx={{
                          fontWeight: 600,
                          color: theme.palette.success.main,
                        }}
                      >
                        {solution.title}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {solution.description}
                      </Typography>
                    </CardContent>
                  </Card>
                ))}
              </Box>
            </Grid>
          </Grid>
        </Container>
      </Box>

      {/* Key Services Section */}
      <Box
        sx={{
          py: { xs: 8, md: 12 },
          background: alpha(theme.palette.primary.main, 0.02),
        }}
      >
        <Container maxWidth="lg">
          <Typography
            variant="h2"
            textAlign="center"
            gutterBottom
            sx={{
              fontWeight: 700,
              fontSize: { xs: "2.5rem", md: "3rem" },
              mb: 2,
            }}
          >
            Key Services for SaaS Platforms
          </Typography>
          <Typography
            variant="h6"
            textAlign="center"
            color="text.secondary"
            sx={{ mb: 8, maxWidth: 600, mx: "auto" }}
          >
            Comprehensive solutions tailored specifically for SaaS business
            models and scaling challenges
          </Typography>

          <Grid container spacing={4}>
            {keyServices.map((service, index) => (
              <Grid size={{ xs: 12, md: 6 }} key={index}>
                <Card
                  sx={{
                    height: "100%",
                    border: `1px solid ${alpha(service.color, 0.2)}`,
                    background: "white",
                    transition: "all 0.3s ease",
                    "&:hover": {
                      transform: "translateY(-8px)",
                      boxShadow: `0 20px 40px ${alpha(service.color, 0.15)}`,
                    },
                  }}
                >
                  <CardContent sx={{ p: 4, textAlign: "center" }}>
                    <Box
                      sx={{
                        color: service.color,
                        mb: 3,
                      }}
                    >
                      {service.icon}
                    </Box>
                    <Typography
                      variant="h5"
                      gutterBottom
                      sx={{ fontWeight: 600, color: service.color }}
                    >
                      {service.title}
                    </Typography>
                    <Typography
                      variant="body1"
                      color="text.secondary"
                      paragraph
                    >
                      {service.description}
                    </Typography>
                    <Box sx={{ textAlign: "left" }}>
                      {service.features.map((feature, featureIndex) => (
                        <Typography
                          key={featureIndex}
                          variant="body1"
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            mb: 0.8,
                            color: "text.primary",
                            fontWeight: 500,
                            "&:before": {
                              content: '"▸"',
                              color: service.color,
                              fontWeight: "bold",
                              mr: 1.5,
                            },
                          }}
                        >
                          {feature}
                        </Typography>
                      ))}
                    </Box>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* Case Study Highlight */}
      <Box sx={{ py: { xs: 8, md: 12 } }}>
        <Container maxWidth="lg">
          <Card
            sx={{
              background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.05)} 0%, ${alpha(theme.palette.secondary.main, 0.05)} 100%)`,
              border: `1px solid ${alpha(theme.palette.primary.main, 0.1)}`,
              p: { xs: 4, md: 6 },
            }}
          >
            <Grid container spacing={4} alignItems="center">
              <Grid size={{ xs: 12, md: 8 }}>
                <Typography variant="h3" gutterBottom sx={{ fontWeight: 700 }}>
                  SaaS Onboarding Automation Success
                </Typography>
                <Typography variant="h6" color="text.secondary" gutterBottom>
                  B2B SaaS Startup - Netherlands
                </Typography>

                <Box sx={{ mb: 4 }}>
                  <Typography
                    variant="body1"
                    color="text.secondary"
                    paragraph
                    sx={{ lineHeight: 1.7 }}
                  >
                    We helped a fast-growing SaaS startup automate their manual
                    onboarding process, reducing administrative overhead and
                    improving customer time-to-value.
                  </Typography>

                  <Box
                    sx={{
                      background: alpha(theme.palette.success.main, 0.1),
                      border: `1px solid ${alpha(theme.palette.success.main, 0.2)}`,
                      borderRadius: 2,
                      p: 3,
                      maxWidth: 400,
                    }}
                  >
                    <Typography
                      variant="h4"
                      gutterBottom
                      sx={{
                        fontWeight: 700,
                        color: theme.palette.success.main,
                      }}
                    >
                      Key Results
                    </Typography>
                    <Grid container spacing={2}>
                      <Grid size={6}>
                        <Typography variant="h5" sx={{ fontWeight: 700 }}>
                          70%
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          Reduction in Manual Onboarding Time
                        </Typography>
                      </Grid>
                      <Grid size={6}>
                        <Typography variant="h5" sx={{ fontWeight: 700 }}>
                          45%
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          Faster Time-to-Value
                        </Typography>
                      </Grid>
                    </Grid>
                  </Box>
                </Box>
              </Grid>

              <Grid size={{ xs: 12, md: 4 }}>
                <Box
                  sx={{
                    background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.secondary.main} 100%)`,
                    color: "white",
                    borderRadius: 2,
                    p: 4,
                    textAlign: "center",
                  }}
                >
                  <Typography
                    variant="h6"
                    gutterBottom
                    sx={{ fontWeight: 600 }}
                  >
                    Ready for Similar Results?
                  </Typography>
                  <PopoverForm
                    triggerElement={
                      <Button
                        variant="contained"
                        size="large"
                        sx={{
                          bgcolor: "white",
                          color: theme.palette.primary.main,
                          mt: 2,
                          px: 3,
                          py: 1,
                          fontWeight: 600,
                          "&:hover": {
                            bgcolor: alpha("#fff", 0.9),
                          },
                        }}
                      >
                        Discuss Your SaaS
                      </Button>
                    }
                  />
                </Box>
              </Grid>
            </Grid>
          </Card>
        </Container>
      </Box>

      {/* Process Section */}
      <Box
        sx={{
          py: { xs: 8, md: 12 },
          backgroundImage: `url('/saas.jpg')`,
          backgroundSize: "cover",
          backgroundPosition: "center",
          backgroundRepeat: "no-repeat",
          position: "relative",
          borderRadius: 5,
        }}
      >
        <Box
          sx={{
            position: "absolute",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: alpha(theme.palette.background.paper, 0.8),
            zIndex: 0,
          }}
        />
        <Container maxWidth="lg" sx={{ position: "relative", zIndex: 1 }}>
          <Typography
            variant="h2"
            textAlign="center"
            gutterBottom
            sx={{
              fontWeight: 700,
              fontSize: { xs: "2.5rem", md: "3rem" },
              mb: 2,
            }}
          >
            Our SaaS Development Process
          </Typography>
          <Typography
            variant="h6"
            textAlign="center"
            color="text.secondary"
            sx={{ mb: 8, maxWidth: 600, mx: "auto" }}
          >
            A proven methodology for building and scaling successful SaaS
            platforms
          </Typography>

          <Stepper orientation="vertical" sx={{ maxWidth: 800, mx: "auto" }}>
            {processSteps.map((step) => (
              <Step key={step.label} active={true}>
                <StepLabel
                  StepIconComponent={() => (
                    <Avatar
                      sx={{
                        bgcolor: theme.palette.primary.main,
                        color: "white",
                        width: 40,
                        height: 40,
                      }}
                    >
                      {step.icon}
                    </Avatar>
                  )}
                >
                  <Typography variant="h5" sx={{ fontWeight: 600 }}>
                    {step.label}
                  </Typography>
                </StepLabel>
                <StepContent>
                  <Typography
                    variant="body1"
                    color="text.secondary"
                    sx={{ mt: 1, lineHeight: 1.7 }}
                  >
                    {step.description}
                  </Typography>
                </StepContent>
              </Step>
            ))}
          </Stepper>
        </Container>
      </Box>

      {/* Why LinXar Labs Section */}
      <Box sx={{ py: { xs: 8, md: 12 } }}>
        <Container maxWidth="lg">
          <Typography
            variant="h2"
            textAlign="center"
            gutterBottom
            sx={{
              fontWeight: 700,
              fontSize: { xs: "2.5rem", md: "3rem" },
              mb: 2,
            }}
          >
            Why Choose LinXar Labs for Your SaaS?
          </Typography>
          <Typography
            variant="h6"
            textAlign="center"
            color="text.secondary"
            sx={{ mb: 8, maxWidth: 600, mx: "auto" }}
          >
            We combine SaaS expertise with technical excellence to drive your
            platform&apos;s success
          </Typography>

          <Grid container spacing={4}>
            {whyChoosePoints.map((point, index) => (
              <Grid size={{ xs: 12, sm: 6 }} key={index}>
                <Card
                  sx={{
                    height: "100%",
                    border: `1px solid ${alpha(theme.palette.primary.main, 0.1)}`,
                    background: "white",
                    transition: "all 0.3s ease",
                    "&:hover": {
                      transform: "translateY(-4px)",
                      boxShadow: `0 12px 30px ${alpha(theme.palette.primary.main, 0.12)}`,
                    },
                  }}
                >
                  <CardContent sx={{ p: 4 }}>
                    <Box
                      sx={{ display: "flex", alignItems: "flex-start", gap: 3 }}
                    >
                      <Box
                        sx={{
                          color: theme.palette.primary.main,
                          flexShrink: 0,
                        }}
                      >
                        {point.icon}
                      </Box>
                      <Box>
                        <Typography
                          variant="h5"
                          gutterBottom
                          sx={{ fontWeight: 600 }}
                        >
                          {point.title}
                        </Typography>
                        <Typography variant="body1" color="text.secondary">
                          {point.description}
                        </Typography>
                      </Box>
                    </Box>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* Final CTA Section */}
      <Box
        sx={{
          background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.05)} 0%, ${alpha(theme.palette.secondary.main, 0.05)} 100%)`,
          py: { xs: 8, md: 12 },
          textAlign: "center",
        }}
      >
        <Container maxWidth="md">
          <Typography
            variant="h2"
            gutterBottom
            sx={{
              fontWeight: 700,
              fontSize: { xs: "2.5rem", md: "3rem" },
              mb: 3,
            }}
          >
            Ready to Scale Your SaaS?
          </Typography>
          <Typography
            variant="h5"
            color="text.secondary"
            sx={{ mb: 6, lineHeight: 1.6 }}
          >
            Let&apos;s discuss how we can help you build, automate, and scale
            your SaaS platform for sustainable growth.
          </Typography>
          <Button
            variant="contained"
            size="large"
            startIcon={<Schedule />}
            onClick={handleConsult}
            sx={{
              px: 4,
              py: 1.5,
              fontSize: "1.1rem",
              fontWeight: 600,
              background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.secondary.main} 100%)`,
              "&:hover": {
                transform: "translateY(-2px)",
                boxShadow: `0 8px 25px ${alpha(theme.palette.primary.main, 0.3)}`,
              },
              transition: "all 0.3s ease",
            }}
          >
            Schedule a Call
          </Button>
        </Container>
      </Box>
    </Box>
  );
}

// Add the missing icon component
// eslint-disable-next-line @typescript-eslint/no-explicit-any
function PriceCheck(props: any) {
  return (
    <svg {...props} viewBox="0 0 24 24" fill="currentColor">
      <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z" />
    </svg>
  );
}
